<template>
  <div>
    <PageHeader :items="items" />
    <validation-observer ref="simpleRules">
      <div class="card">
        <div class="card-body">
          <div class="row mb-2">
            <div class="col-md-6">
              <validation-provider
                #default="{ errors }"
                name="Title"
                rules="required"
              >
                <TextInput
                  v-model="form.title"
                  type="text"
                  :label="$t('title')"
                  :tooltipMessage="$t('title')"
                  :required="true"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </div>
            <div class="col-md-6">
              <TextInput
                v-model="form.scope"
                type="text"
                :label="$t('scope')"
                :tooltipMessage="$t('scope')"
              />
            </div>
          </div>
          <div class="row mb-2">
            <div class="col-md-6">
              <TextInput
                v-model="form.value"
                type="text"
                :label="$t('value')"
                :tooltipMessage="$t('value')"
              />
            </div>
            <div class="col-md-6">
              <label class="form-label" for="needs_permission">{{
                $t("needsPermission")
              }}</label>
              <MultiSelectInput
                v-model="form.needs_permission"
                :options="permissions"
                placeholder="selectOption"
                label="title"
                trackBy="id"
                :key="form.needs_permission"
                moduleName="permissions"
                :searchParamName="`search_string`"
                :multiple="false"
              >
              </MultiSelectInput>
            </div>
            <div class="col-md-6">
              <label class="form-label" for="cars"> {{ $t("company") }} </label>
              <MultiSelectInput
                v-model="form.company"
                :options="customers"
                :query="{ isMultiSelect: true }"
                label="companyName"
                trackBy="id"
                :key="form.company"
                moduleName="customers"
                :multiple="false"
                ><template #option="{ props }">
                  <div>
                    <p>
                      {{ props.option.companyNumber }} -
                      {{ props.option.companyName }}
                    </p>
                  </div>
                </template>
              </MultiSelectInput>
            </div>
            <div class="col-md-6">
              <label class="form-label" for="needs_permission">{{
                $t("groups")
              }}</label>
              <multi-select
                v-model="form.grouping"
                :options="groupings"
                :multiple="false"
                label="grouping"
                trackBy="grouping"
                selectLabel=""
                deselectLabel=""
                open-direction="bottom"
              />
            </div>
          </div>
          <div class="row mb-2">
            <div class="col-md-6">
              <label class="form-label" for="cars">{{
                $t("canBeAssigned")
              }}</label>
              <input
                id="can_be_assigned"
                class="form-check-input-custom ml-2"
                type="checkbox"
                v-model="form.can_be_assigned"
                name="can_be_assigned"
              />
            </div>
            <div class="col-md-6">
              <label class="form-label" for="cars">{{ $t("active") }}</label>

              <input
                id="active"
                class="form-check-input-custom ml-2"
                v-model="form.active"
                type="checkbox"
              />
            </div>
          </div>
          <div class="row mb-2">
            <div class="col-md-6">
              <label class="form-label" for="cars">{{
                $t("availableForAllTenants")
              }}</label>
              <input
                id="is_all_tenants"
                class="form-check-input-custom ml-2"
                type="checkbox"
                v-model="form.is_all_tenants"
                name="is_all_tenants"
              />
            </div>
            <div class="col-md-6">
              <label class="form-label" for="cars">{{
                $t("availableForAllCompanies")
              }}</label>

              <input
                id="is_all_companies"
                class="form-check-input-custom ml-2"
                v-model="form.is_all_companies"
                type="checkbox"
                name="is_all_companies"
              />
            </div>
          </div>
          <div class="row">
            <div class="col-md-12">
              <div class="form-group">
                <label class="form-label"> {{ $t("description") }} </label>
                <textarea
                  id="description"
                  rows="5"
                  class="form-control"
                  name="description"
                  v-model="form.description"
                />
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-md-6">
              <button
                v-if="!group && !form.grouping"
                @click="addGroup"
                class="btn btn-primary d-block my-3"
              >
                + {{ $t("addGroup") }}
              </button>
              <Group
                v-if="group && !form.grouping"
                @deleteParentGroup="group = null"
                :isOriginal="true"
                @group="group = $event"
                :triggered="triggered"
                :group="group"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="d-flex align-items-center justify-content-end">
        <b-button
          variant="primary"
          @click="create"
          class="d-flex align-items-center gap-1"
          :disabled="isFormInvalid"
        >
          <feather-icon icon="PlusIcon" size="12" />
          <span>{{ $t("createPermission") }}</span>
        </b-button>
      </div>
    </validation-observer>
  </div>
</template>

<script>
import TextInput from "@/components/TextInput.vue";
import PageHeader from "@/components/PageHeader.vue";
import MultiSelectInput from "@/components/MultiSelectInput.vue";
import MultiSelect from "vue-multiselect";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { mapGetters } from "vuex";
import { required, email } from "@validations";
import Group from "@/views/permissions/Group.vue";
import formDirtyMixin from "@/mixins/formDirtyMixin";

export default {
  components: {
    TextInput,
    PageHeader,
    MultiSelect,
    Group,
    MultiSelectInput,
    ValidationProvider,
    ValidationObserver,
  },
  mixins: [formDirtyMixin],
  computed: {
    ...mapGetters("permissions", ["permissions", "groupings"]),
    ...mapGetters("customers", ["customers"]),
    items() {
      return [
        {
          text: this.$t("dentalTwin"),
          to: "/home",
        },
        {
          text: this.$t("settings"),
          to: "/settings",
        },
        {
          text: this.$t("permissions"),
          to: "/permissions",
        },
        {
          text: this.$t("create"),
          active: true,
        },
      ];
    },
    isFormInvalid() {
      return !this.form.title;
    },
  },
  data() {
    return {
      originalForm: null,
      options: [],
      form: {
        title: "",
        needs_permission: null,
        can_be_assigned: false,
        active: false,
        scope: "",
        value: "",
        grouping: "",
        description: "",
        is_all_tenants: false,
        is_all_companies: false,
      },
      triggered: false,
      group: null,
    };
  },
  watch: {
    form: {
      handler(newVal) {
        this.isFormDirty = JSON.stringify(newVal) !== this.originalForm;
      },
      deep: true,
    },
  },
  async mounted() {
    try {
      this.$store.commit("showLoader", true);
      await this.$store.dispatch("customers/list", { isMultiSelect: true });
      if (!this.permissions?.length)
        await this.$store.dispatch("permissions/list", {
          limit_start: 0,
          limit_count: 25,
        });
      if (!this.groupings?.length)
        await this.$store.dispatch("permissions/listGroupings", {
          limit_start: 0,
          limit_count: 100,
        });
      this.originalForm = JSON.stringify(this.form);
    } catch (e) {
      console.log(e);
    } finally {
      this.shouldShow = true;
      this.$store.commit("showLoader", false);
    }
  },

  methods: {
    /**
     * adds a group
     */
    addGroup() {
      this.group = {
        name: "",
        child: null,
      };
    },
    /**
     * reccursively checks if a group has an empty display name or value if yes then return true
     * @param {group} the group that we want to check error for
     * @returns boolean
     */
    checkForGroupError(group) {
      if (!group) return false;
      let hasError = false;
      if (!group.name) {
        hasError = true;
      } else {
        if (group.child) {
          hasError = this.checkForGroupError(group.child);
        }
      }
      return hasError;
    },
    /**
     * makes a group path string and returns it
     * this method is called recursively since we can have child and sub childs and so on
     * we traverse through all the sub groups to create a group path
     * @param {group} group object that has a 'name' and 'child' in it
     */
    getGroupPath(group) {
      if (!group) return "";
      let groupPath = "";
      groupPath += group.name;
      if (group.child) {
        groupPath += "/" + this.getGroupPath(group.child);
      }
      return groupPath;
    },
    async create() {
      this.triggered = true;
      if (!this.checkForGroupError(this.group)) {
        try {
          this.$refs.simpleRules.validate().then(async (success) => {
            if (success) {
              const payload = {
                ...this.form,
                needs_permission: this.form.needs_permission?.id ?? null,
                set_company_id: this.form.company?.id ?? "",
                can_be_assigned: this.form.can_be_assigned === true ? 1 : 0,
                active: this.form.active === true ? 1 : 0,
                is_all_tenants: this.form.is_all_tenants === true ? 1 : 0,
                is_all_companies: this.form.is_all_companies === true ? 1 : 0,
                grouping:
                  this.form.grouping?.grouping ?? this.getGroupPath(this.group),
              };

              let response = await this.$store.dispatch(
                "permissions/create",
                payload
              );
              if (response?.message_type !== "error") {
                this.isFormDirty = false;
                this.originalForm = JSON.stringify(this.form);
                await this.$router.push("/permissions");
              }
            }
          });
        } catch (e) {}
      }
    },
  },
};
</script>

<style scoped>
#nprogress {
  position: relative;
  z-index: 9999999;
}

.white-color {
  color: white !important;
}

.vgt-responsive {
  overflow-x: visible !important;
}
</style>
